import { BarChart3, CheckCircle2, Eye, FileText, Globe2, Inbox } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useDashboardMetrics } from '../../hooks/useLeadCapture';
import BookingDashboardCard from '../../components/booking/BookingDashboardCard';
import BookingStatsCard from '../../components/booking/BookingStatsCard';
import Spinner from '../../components/Spinner';
import { FormType } from '../../types/leadForms';
import { Link } from 'react-router-dom';

// const PIE_COLORS = ['#002144', '#801414', '#2563eb', '#10b981', '#f59e0b', '#64748b'];
const FORM_TYPE_LABELS: Record<FormType, string> = {
  [FormType.LeadCapture]: 'Lead Capture',
  [FormType.Newsletter]: 'Newsletter',
  [FormType.ContactUs]: 'Contact Us',
  [FormType.RequestDemo]: 'Request Demo',
  [FormType.RequestQuote]: 'Request Quote',
  [FormType.WebinarRegistration]: 'Webinar Registration',
  [FormType.EventRegistration]: 'Event Registration',
  [FormType.Feedback]: 'Feedback',
  [FormType.SupportRequest]: 'Support Request',
  [FormType.Custom]: 'Custom',
};
const CHART_COLORS = [
  '#172554',
  '#7f1d1d',
  '#2563eb',
  '#10b981',
  '#f59e0b',
  '#64748b',
];

export default function LeadDashboardPage() {
  const { data: metrics, isLoading, isError, } = useDashboardMetrics();  
  if (isError || !metrics) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Unable to load Forms dashboard.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Spinner size="h-8 w-8" />
        <p className="mt-3 text-strivre-mutedtext">Loading lead dashboard...</p>
      </div>
    );
  }

  const formTypeData = metrics.submissionsByFormType.map((item) => ({
    ...item,

    name:
      FORM_TYPE_LABELS[item.formType] ??
      item.formTypeName,
  }));

  const trendData = metrics.submissionTrend.map((item) => ({
    ...item,

    label: new Date(item.date).toLocaleDateString(
      undefined,
      {
        month: 'short',
        day: 'numeric',
      }
    ),
  }));

  return (
    <div className="space-y-6 text-left">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-strivre-navy">
          <BarChart3 size={22} />
          Forms Dashboard
        </h1>

        <p className="mt-1 text-sm text-strivre-mutedtext">
          Track form activity, submission volume, and engagement across your Forms Platform.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <BookingStatsCard
          title="Total Forms"
          value={metrics.totalForms}
          icon={<FileText size={20} />}
        />

        <BookingStatsCard
          title="Active Forms"
          value={metrics.activeForms}
          icon={<CheckCircle2 size={20} />}
        />

        <BookingStatsCard
          title="Public Forms"
          value={metrics.publicForms}
          icon={<Globe2 size={20} />}
        />

        <BookingStatsCard
          title="Total Submissions"
          value={metrics.totalSubmissions}
          icon={<Inbox size={20} />}
        />

        <BookingStatsCard
          title="This Month"
          value={metrics.submissionsThisMonth}
          icon={<BarChart3 size={20} />}
        />

        <BookingStatsCard
          title="Today"
          value={metrics.submissionsToday}
          icon={<Eye size={20} />}
          highlight
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <BookingDashboardCard title="Submissions by Form">
          {metrics?.submissionsByForm.length === 0 ? (
            <p className="py-12 text-center text-sm text-strivre-mutedtext">
              No submission data available.
            </p>
          ) : (
            <ResponsiveContainer
              width="100%"
              height={280}
            >
              <BarChart
                data={metrics.submissionsByForm}
                layout="vertical"
                margin={{
                  top: 5,
                  right: 20,
                  bottom: 5,
                  left: 20,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#E8EEF4"
                />

                <XAxis
                  type="number"
                  allowDecimals={false}
                  tick={{
                    fontSize: 11,
                    fill: '#64748B',
                  }}
                />

                <YAxis
                  type="category"
                  dataKey="formName"
                  width={110}
                  tick={{
                    fontSize: 11,
                    fill: '#64748B',
                  }}
                />

                <Tooltip />

                <Bar
                  dataKey="count"
                  fill="#172554"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </BookingDashboardCard>

        <BookingDashboardCard title="Submissions by Form Type">
          {formTypeData.length === 0 ? (
            <p className="py-12 text-center text-sm text-strivre-mutedtext">
              No form type data available.
            </p>
          ) : (
            <ResponsiveContainer
              width="100%"
              height={280}
            >
              <PieChart>
                <Pie
                  data={formTypeData}
                  dataKey="count"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={4}
                >
                  {formTypeData.map((item, index) => (
                    <Cell
                      key={`${item.formType}-${index}`}
                      fill={
                        CHART_COLORS[
                          index % CHART_COLORS.length
                        ]
                      }
                    />
                  ))}
                </Pie>

                <Tooltip />

                <Legend
                  iconType="circle"
                  wrapperStyle={{
                    fontSize: '12px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </BookingDashboardCard>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <BookingDashboardCard title="Submission Activity - Last 30 Days">
          <ResponsiveContainer
            width="100%"
            height={280}
          >
            <LineChart data={trendData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E8EEF4"
              />

              <XAxis
                dataKey="label"
                tick={{
                  fontSize: 10,
                  fill: '#64748B',
                }}
                interval="preserveStartEnd"
              />

              <YAxis
                allowDecimals={false}
                tick={{
                  fontSize: 11,
                  fill: '#64748B',
                }}
              />

              <Tooltip />

              <Line
                type="monotone"
                dataKey="count"
                stroke="#7f1d1d"
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 5,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </BookingDashboardCard>
        
        <BookingDashboardCard title="Recent Submissions">
          {metrics.recentSubmissions.length === 0 ? (
            <p className="py-12 text-center text-sm text-strivre-mutedtext">
              No recent submissions.
            </p>
          ) : (
            <div className="divide-y divide-strivre-lightnavy">
              {metrics.recentSubmissions.map((submission) => (
                <div
                  key={submission.id}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-strivre-darktext">
                      {submission.formName}
                    </p>

                    <p className="mt-0.5 text-xs text-strivre-mutedtext">
                      {new Date(
                        submission.submittedDate
                      ).toLocaleString()}
                    </p>
                  </div>

                  <Link
                    to={`/forms/${submission.formId}/submissions`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-strivre-burgundy"
                  >
                    <Eye size={14} />
                    View
                  </Link>
                </div>
              ))}
            </div>
          )}
        </BookingDashboardCard>
      </div>
    </div>
  );
}
